import { z } from 'zod';
import type { FormField, FormSection } from '@/shared/types/form';

/**
 * API 필드 설정에서 Zod 스키마 자동 생성
 *
 * **핵심 개념**:
 * - FormField (API response) → z.ZodTypeAny (런타임 검증)
 * - 각 필드 타입별로 적절한 Zod 스키마 생성
 * - validation 객체를 Zod 메서드로 변환 (min, max, pattern 등)
 *
 * **사용 예시**:
 * ```ts
 * const field: FormField = {
 *   type: 'text',
 *   name: 'username',
 *   required: true,
 *   validation: { minLength: 3, maxLength: 20 }
 * };
 *
 * const schema = createFieldSchema(field);
 * schema.parse('john'); // ✅ 통과
 * schema.parse('ab');   // ❌ 실패: 최소 3자
 * ```
 */
export function createFieldSchema(field: FormField): z.ZodTypeAny {
  let schema: z.ZodTypeAny;

  switch (field.type) {
    case 'text':
    case 'textarea':
      schema = z.string();
      if (field.validation?.minLength) {
        schema = (schema as z.ZodString).min(
          field.validation.minLength,
          `최소 ${field.validation.minLength}자 이상 입력하세요`,
        );
      }
      if (field.validation?.maxLength) {
        schema = (schema as z.ZodString).max(
          field.validation.maxLength,
          `최대 ${field.validation.maxLength}자까지 입력 가능합니다`,
        );
      }
      if (field.validation?.pattern) {
        schema = (schema as z.ZodString).regex(
          new RegExp(field.validation.pattern),
          '형식이 올바르지 않습니다',
        );
      }
      break;

    case 'url':
      schema = z.string().url('유효한 URL을 입력하세요 (예: https://example.com)');
      break;

    case 'number':
      schema = z.coerce.number({ message: '숫자를 입력하세요' });
      if (field.validation?.min !== undefined) {
        schema = (schema as z.ZodNumber).min(
          field.validation.min,
          `최소값은 ${field.validation.min}입니다`,
        );
      }
      if (field.validation?.max !== undefined) {
        schema = (schema as z.ZodNumber).max(
          field.validation.max,
          `최대값은 ${field.validation.max}입니다`,
        );
      }
      break;

    case 'select':
    case 'dropdown':
    case 'radio':
      if (field.options && field.options.length > 0) {
        const enumValues = field.options.map((o) => o.value) as [string, ...string[]];
        schema = z.enum(enumValues, { message: '유효한 옵션을 선택하세요' });
      } else {
        schema = z.string();
      }
      break;

    case 'checkbox':
      schema = z.boolean({ message: '필수 항목입니다' });
      break;

    case 'file':
      // TODO: File 타입 검증 (MIME type, size 등)
      schema = z.any();
      break;

    case 'slider':
      schema = z
        .number()
        .min(field.validation?.min ?? 0)
        .max(field.validation?.max ?? 100);
      break;

    default:
      schema = z.any();
  }

  // Required 검증 (optional이면 undefined 허용)
  if (!field.required) {
    schema = schema.optional();
  }

  return schema;
}

/**
 * 전체 폼 스키마 생성 (모든 섹션의 필드를 통합)
 *
 * **TanStack Form 통합**:
 * ```tsx
 * const form = useForm({
 *   validators: {
 *     onChange: createFormSchema(formSchema.sections),
 *   },
 * });
 * ```
 *
 * @param sections - API에서 받은 폼 섹션 배열
 * @returns Zod 객체 스키마
 */
export function createFormSchema(sections: FormSection[]) {
  const schemaShape: Record<string, z.ZodTypeAny> = {};

  for (const section of sections) {
    for (const field of section.fields) {
      schemaShape[field.name] = createFieldSchema(field);
    }
  }

  return z.object(schemaShape);
}
